import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchDividendStockPriceHistory } from '@apis/dividends';
import type { DividendStockPriceHistoryCsv } from '@app-types/dividendApi';
import { getAccessToken } from '@utils/authStorage';
import { getIframeQueryParamsFromSearch } from '@utils/queryParams';

const YYYYMMDD = /^\d{8}$/;

export const buildDividendStockPriceHistoryQueryKey = (
  ticker: string | undefined,
  mkt: string | undefined,
  from: string | null,
  to: string | null,
  interval?: 'd',
) => [
  'dividends',
  'dividend-stock-price-history',
  ticker,
  mkt,
  from,
  to,
  interval,
] as const;

/**
 * React Query hook for the dividend stock-price history CSV
 * (GET /api/dividends/dividend-transition/chart5-hts). `from`/`to` bound the
 * selected visible annual range and are part of the query key so switching the
 * 5/10 period does not reuse stale CSV data.
 *
 * The hook is always called; pass `null` for `from`/`to` until the transition
 * data has enough annual rows to derive a range. It stays disabled until a
 * valid 8-digit range and instrument context (plus token) are available.
 */
export const useDividendStockPriceHistory = (
  from: string | null,
  to: string | null,
  interval?: 'd',
) => {
  const location = useLocation();
  const { ticker, mkt } = getIframeQueryParamsFromSearch(location.search);
  const hasToken = Boolean(getAccessToken());
  const hasRange = Boolean(from && to && YYYYMMDD.test(from) && YYYYMMDD.test(to));

  return useQuery<DividendStockPriceHistoryCsv>({
    queryKey: buildDividendStockPriceHistoryQueryKey(
      ticker,
      mkt,
      from,
      to,
      interval,
    ),
    queryFn: () =>
      fetchDividendStockPriceHistory({
        ticker: ticker as string,
        mkt: mkt as string,
        from: from as string,
        to: to as string,
        interval,
      }),
    enabled: Boolean(ticker && mkt && hasToken && hasRange),
    staleTime: 60_000,
    retry: 1,
  });
};
